import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { ArrowLeft, Plus, Sparkles } from "lucide-react";

import { Schermbouwer } from "./Schermbouwer";
import type { OntwerpSamenvatting } from "./ontwerpTypes";
import "./schermbouwer.css";

/**
 * De ingang van de AI-schermbouwer: je ontwerpen, en een nieuwe beginnen.
 *
 * <p>Bewust een lijst en geen lege chat. Wie hier binnenkomt is meestal niet nieuw maar terug -- om
 * een tegel bij te schaven of te kijken wat een collega gemaakt heeft. Een scherm dat meteen om een
 * opdracht vraagt, verbergt precies dat.
 *
 * <p>De lijst is organisatiebreed (besluit 11.5): tegels zijn eigendom van de organisatie, niet van
 * de maker. Dat is ook waarom er geen "mijn tegels" staat; het zou suggereren dat er ergens tegels
 * van iemand anders zijn die je niet mag zien, en die zijn er niet.
 */
export function SchermbouwerPagina() {
  const t = useTranslate();
  const { mutateAsync } = useCustomMutation();
  const [open, setOpen] = useState<OntwerpSamenvatting | null>(null);
  const [naam, setNaam] = useState("");
  const [type, setType] = useState("");
  const [bezig, setBezig] = useState(false);

  const { data: typesData } = useCustom<string[]>({ url: "schermbouwer/schermtypes", method: "get" });
  const { data: lijstData, refetch } = useCustom<OntwerpSamenvatting[]>({
    url: "schermbouwer/ontwerpen",
    method: "get",
  });

  const schermtypes = typesData?.data ?? [];
  const ontwerpen = lijstData?.data ?? [];
  const gekozenType = type || schermtypes[0] || "";

  async function begin() {
    if (schermtypes.length === 0 || bezig) return;
    setBezig(true);
    try {
      const antwoord = await mutateAsync({
        url: "schermbouwer/ontwerpen",
        method: "post",
        values: { schermtype: gekozenType, naam: naam.trim() || t("schermbouwer.nieuweTegel") },
        successNotification: false,
        errorNotification: false,
      });
      setNaam("");
      await refetch();
      setOpen(antwoord?.data as unknown as OntwerpSamenvatting);
    } finally {
      setBezig(false);
    }
  }

  if (open) {
    return (
      <div className="sb__kolom">
        <div className="sb__voorbeeldbalk">
          <Button variant="secondary" onClick={() => { setOpen(null); void refetch(); }}>
            <ArrowLeft size={15} aria-hidden="true" />
            {t("schermbouwer.terugNaarLijst")}
          </Button>
          <span className="sb__kaarttitel">{open.naam}</span>
        </div>
        <Schermbouwer ontwerpId={open.id} schermtype={open.schermtype} />
      </div>
    );
  }

  return (
    <div className="sb__kolom">
      <div className="sb__kaart">
        <span className="sb__kaarttitel">
          <Sparkles size={16} aria-hidden="true" />
          {t("schermbouwer.nieuweTegelMaken")}
        </span>
        <p className="sb__waarschuwing">{t("schermbouwer.ingangUitleg")}</p>
        <div className="sb__invoerbalk">
          {/* Het tegeltype bepaalt de catalogus, en daarmee alles wat je straks kunt kiezen. Daarom
              staat de keuze hier en niet ergens in het bouwscherm: achteraf wisselen zou elk
              onderdeel ongeldig kunnen maken. */}
          <select
            value={gekozenType}
            aria-label={t("schermbouwer.schermtype")}
            onChange={(e) => setType(e.target.value)}
          >
            {schermtypes.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
          <input
            value={naam}
            placeholder={t("schermbouwer.naamPlaatshouder")}
            aria-label={t("schermbouwer.naam")}
            onChange={(e) => setNaam(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void begin(); }}
          />
          <Button variant="primary" disabled={bezig || schermtypes.length === 0} onClick={() => void begin()}>
            <Plus size={15} aria-hidden="true" />
            {t("schermbouwer.beginnen")}
          </Button>
        </div>
        {schermtypes.length === 0 && (
          <p className="sb__waarschuwing">{t("schermbouwer.geenCatalogus")}</p>
        )}
      </div>

      <div className="sb__kaart">
        <span className="sb__kaarttitel">{t("schermbouwer.bestaandeTegels")}</span>
        {ontwerpen.length === 0 ? (
          <p className="sb__waarschuwing">{t("schermbouwer.nogGeenTegels")}</p>
        ) : (
          <ul className="sb__versies">
            {ontwerpen.map((o) => (
              <li key={o.id} className="sb__versie">
                <span className="sb__versie__wat">
                  <b>{o.naam}</b>
                  <br />
                  <span className="sb__waarschuwing">
                    {t(`schermbouwer.status.${o.status}`)} · v{o.huidigeVersie} · {o.tegelCode}
                  </span>
                </span>
                <Button variant="secondary" onClick={() => setOpen(o)}>
                  {t("schermbouwer.openen")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
